import React, { useState } from 'react';
import { X as XIcon, Mail as MailIcon, Linkedin as LinkedinIcon, Tag as TagIcon, ThumbsUpIcon, MessageSquareIcon, CalendarIcon, BriefcaseIcon, MapPinIcon, ChevronDownIcon, ChevronUpIcon, PhoneIcon, ClockIcon, BarChart2Icon, StarIcon } from 'lucide-react';
import { peopleProfiles, linkedInPosts } from '../utils/mockData';
interface PersonModalProps {
  personId: number;
  onClose: () => void;
  onNavigateToMessages: (personId: number) => void;
}
export const PersonModal: React.FC<PersonModalProps> = ({
  personId,
  onClose,
  onNavigateToMessages
}) => {
  const [activeTab, setActiveTab] = useState<'engagement' | 'info' | 'notes'>('engagement');
  const [notes, setNotes] = useState('');
  const [showAllInteractions, setShowAllInteractions] = useState(false);
  const person = peopleProfiles.find(p => p.id === personId);
  if (!person) return null;
  const getPostById = (postId: number) => {
    return linkedInPosts.find(post => post.id === postId);
  };
  // Calculate engagement metrics
  const engagementMetrics = {
    total: person.interactions.length,
    likes: person.interactions.filter(i => i.type === 'liked').length,
    comments: person.interactions.filter(i => i.type === 'commented').length,
    shares: person.interactions.filter(i => i.type === 'shared').length,
    recentActivity: person.interactions.some(i => i.date.includes('day') && parseInt(i.date) <= 3)
  };
  // Get displayed interactions based on toggle state
  const displayedInteractions = showAllInteractions ? person.interactions : person.interactions.slice(0, 3);
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-id="element-735">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-736">
        <div className="flex justify-between items-center p-6 border-b border-gray-200" data-id="element-737">
          <h2 className="text-xl font-semibold text-gray-900" data-id="element-738">
            Contact Profile
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-739">
            <XIcon size={20} data-id="element-740" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto" data-id="element-741">
          <div className="p-6" data-id="element-742">
            <div className="flex flex-col md:flex-row md:items-start" data-id="element-743">
              <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6" data-id="element-744">
                <img src={person.image} alt={person.name} className="w-24 h-24 rounded-full object-cover border-2 border-gray-200" data-id="element-745" />
              </div>
              <div className="flex-1" data-id="element-746">
                <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-747">
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-748">
                    {person.name}
                  </h3>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${person.relevance === 'high' ? 'bg-green-100 text-green-800' : person.relevance === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-749">
                    {person.relevance.charAt(0).toUpperCase() + person.relevance.slice(1)}{' '}
                    Intent
                  </span>
                </div>
                <p className="text-md text-gray-600" data-id="element-750">{person.title}</p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-751">
                  <div className="flex items-center text-gray-600" data-id="element-752">
                    <BriefcaseIcon size={16} className="mr-2" data-id="element-753" />
                    <span data-id="element-754">{person.company}</span>
                  </div>
                  <div className="flex items-center text-gray-600" data-id="element-755">
                    <MapPinIcon size={16} className="mr-2" data-id="element-756" />
                    <span data-id="element-757">San Francisco, CA</span>
                  </div>
                  <div className="flex items-center text-gray-600" data-id="element-758">
                    <CalendarIcon size={16} className="mr-2" data-id="element-759" />
                    <span data-id="element-760">Connected 2 weeks ago</span>
                  </div>
                  <div className="flex items-center text-gray-600" data-id="element-761">
                    <ClockIcon size={16} className="mr-2" data-id="element-762" />
                    <span data-id="element-763">Last active: 3 days ago</span>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2" data-id="element-764">
                  {person.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-765">
                      <TagIcon size={12} className="mr-1" data-id="element-766" />
                      {tag}
                    </span>)}
                </div>
              </div>
            </div>
            {/* Intent Score Card */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-767">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between" data-id="element-768">
                <div className="mb-3 sm:mb-0" data-id="element-769">
                  <h4 className="text-sm font-medium text-gray-700 flex items-center" data-id="element-770">
                    <BarChart2Icon size={16} className="mr-1.5" data-id="element-771" />
                    Intent Signals
                  </h4>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-772">
                    Based on {engagementMetrics.total} interactions over the
                    last 30 days
                  </p>
                </div>
                <div className="flex items-center space-x-4" data-id="element-773">
                  <div className="flex flex-col items-center" data-id="element-774">
                    <div className="text-xs text-gray-500 mb-1" data-id="element-775">
                      Engagement Score
                    </div>
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-776">
                      <span className={`text-sm font-medium ${person.relevance === 'high' ? 'text-green-600' : person.relevance === 'medium' ? 'text-[#f9b30f]' : 'text-gray-600'}`} data-id="element-777">
                        {person.relevance === 'high' ? '8.2' : person.relevance === 'medium' ? '6.4' : '3.5'}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-center" data-id="element-778">
                    <div className="text-xs text-gray-500 mb-1" data-id="element-779">
                      Interest Level
                    </div>
                    <div className="flex" data-id="element-780">
                      {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} size={16} className={star <= (person.relevance === 'high' ? 5 : person.relevance === 'medium' ? 3 : 1) ? 'text-[#f9b30f]' : 'text-gray-300'} fill={star <= (person.relevance === 'high' ? 5 : person.relevance === 'medium' ? 3 : 1) ? '#f9b30f' : 'none'} data-id="element-781" />)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center" data-id="element-782">
                <div className="p-2 bg-white rounded border border-gray-100" data-id="element-783">
                  <div className="text-xs text-gray-500" data-id="element-784">Total</div>
                  <div className="font-medium" data-id="element-785">{engagementMetrics.total}</div>
                </div>
                <div className="p-2 bg-white rounded border border-gray-100" data-id="element-786">
                  <div className="text-xs text-gray-500" data-id="element-787">Likes</div>
                  <div className="font-medium" data-id="element-788">{engagementMetrics.likes}</div>
                </div>
                <div className="p-2 bg-white rounded border border-gray-100" data-id="element-789">
                  <div className="text-xs text-gray-500" data-id="element-790">Comments</div>
                  <div className="font-medium" data-id="element-791">
                    {engagementMetrics.comments}
                  </div>
                </div>
                <div className="p-2 bg-white rounded border border-gray-100" data-id="element-792">
                  <div className="text-xs text-gray-500" data-id="element-793">Shares</div>
                  <div className="font-medium" data-id="element-794">{engagementMetrics.shares}</div>
                </div>
              </div>
            </div>
            {/* Tabs */}
            <div className="mt-6 border-b border-gray-200" data-id="element-795">
              <nav className="-mb-px flex space-x-6" data-id="element-796">
                <button onClick={() => setActiveTab('engagement')} className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'engagement' ? 'border-[#f9b30f] text-[#f9b30f]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-797">
                  Engagement History
                </button>
                <button onClick={() => setActiveTab('info')} className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'info' ? 'border-[#f9b30f] text-[#f9b30f]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-798">
                  Contact Info
                </button>
                <button onClick={() => setActiveTab('notes')} className={`whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'notes' ? 'border-[#f9b30f] text-[#f9b30f]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-799">
                  Notes
                </button>
              </nav>
            </div>
            {activeTab === 'engagement' && <div className="mt-4" data-id="element-800">
                <div className="space-y-4" data-id="element-801">
                  {displayedInteractions.map((interaction, index) => {
                const post = getPostById(interaction.postId);
                return <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-802">
                        <div className="flex items-start" data-id="element-803">
                          <div className="flex-shrink-0 mt-1" data-id="element-804">
                            {interaction.type === 'liked' ? <div className="p-1.5 bg-blue-100 rounded-full" data-id="element-805">
                                <ThumbsUpIcon size={14} className="text-blue-600" data-id="element-806" />
                              </div> : interaction.type === 'commented' ? <div className="p-1.5 bg-purple-100 rounded-full" data-id="element-807">
                                <MessageSquareIcon size={14} className="text-purple-600" data-id="element-808" />
                              </div> : <div className="p-1.5 bg-green-100 rounded-full" data-id="element-809">
                                <MessageSquareIcon size={14} className="text-green-600" data-id="element-810" />
                              </div>}
                          </div>
                          <div className="ml-3 flex-1" data-id="element-811">
                            <p className="text-sm text-gray-600" data-id="element-812">
                              <span className="font-medium" data-id="element-813">
                                {interaction.type === 'liked' ? 'Liked' : interaction.type === 'commented' ? 'Commented on' : 'Shared'}
                              </span>{' '}
                              a post by {post?.author} {interaction.date}
                            </p>
                            <div className="mt-1 text-sm text-gray-800 bg-white p-2 rounded border border-gray-200" data-id="element-814">
                              "{post?.content.substring(0, 100)}..."
                            </div>
                            {interaction.comment && <div className="mt-2" data-id="element-815">
                                <p className="text-xs text-gray-500" data-id="element-816">
                                  Their comment:
                                </p>
                                <p className="mt-1 text-sm bg-white p-2 rounded border border-gray-200 italic" data-id="element-817">
                                  "{interaction.comment}"
                                </p>
                              </div>}
                          </div>
                        </div>
                      </div>;
              })}
                </div>
                {person.interactions.length > 3 && <button onClick={() => setShowAllInteractions(!showAllInteractions)} className="mt-3 text-sm text-[#f9b30f] hover:text-[#e0a40e] flex items-center mx-auto" data-id="element-818">
                    {showAllInteractions ? <>
                        Show less
                        <ChevronUpIcon size={16} className="ml-1" data-id="element-819" />
                      </> : <>
                        Show all {person.interactions.length} interactions
                        <ChevronDownIcon size={16} className="ml-1" data-id="element-820" />
                      </>}
                  </button>}
              </div>}
            {activeTab === 'info' && <div className="mt-4" data-id="element-821">
                <h4 className="text-lg font-medium text-gray-900 mb-4" data-id="element-822">
                  Contact Information
                </h4>
                <div className="space-y-4" data-id="element-823">
                  <div className="flex items-center" data-id="element-824">
                    <MailIcon size={16} className="text-gray-400 mr-2" data-id="element-825" />
                    <span className="text-gray-600" data-id="element-826">
                      {person.name.toLowerCase().replace(' ', '.')}@
                      {person.company.toLowerCase().replace(' ', '')}.com
                    </span>
                  </div>
                  <div className="flex items-center" data-id="element-827">
                    <LinkedinIcon size={16} className="text-gray-400 mr-2" data-id="element-828" />
                    <span className="text-gray-600" data-id="element-829">
                      linkedin.com/in/
                      {person.name.toLowerCase().replace(' ', '-')}
                    </span>
                  </div>
                  <div className="flex items-center" data-id="element-830">
                    <PhoneIcon size={16} className="text-gray-400 mr-2" data-id="element-831" />
                    <span className="text-gray-600" data-id="element-832">+1 (555) 123-4567</span>
                  </div>
                  <div className="mt-6" data-id="element-833">
                    <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-834">
                      Company Information
                    </h5>
                    <div className="p-3 bg-gray-50 rounded border border-gray-200" data-id="element-835">
                      <div className="flex items-center mb-2" data-id="element-836">
                        <BriefcaseIcon size={14} className="text-gray-400 mr-2" data-id="element-837" />
                        <span className="text-gray-800 font-medium" data-id="element-838">
                          {person.company}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600" data-id="element-839">
                        {person.company} is a leading provider of software
                        solutions for business intelligence and data analytics.
                        Founded in 2015, they specialize in helping enterprises
                        leverage their data for strategic decision-making.
                      </p>
                      <div className="mt-2 text-xs text-gray-500" data-id="element-840">
                        Industry: Software / Analytics • 50-200 employees •
                        Series B
                      </div>
                    </div>
                  </div>
                </div>
              </div>}
            {activeTab === 'notes' && <div className="mt-4" data-id="element-841">
                <h4 className="text-lg font-medium text-gray-900 mb-4" data-id="element-842">
                  Notes
                </h4>
                <textarea className="w-full border border-gray-300 rounded-md p-3 text-sm" rows={5} placeholder="Add your notes about this contact..." value={notes} onChange={e => setNotes(e.target.value)} data-id="element-843"></textarea>
                <div className="mt-3 text-right" data-id="element-844">
                  <button className="px-3 py-1.5 bg-[#f9b30f] hover:bg-[#e0a40e] text-white text-sm font-medium rounded" onClick={() => alert('Notes saved!')} data-id="element-845">
                    Save Notes
                  </button>
                </div>
              </div>}
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3" data-id="element-846">
          <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" onClick={onClose} data-id="element-847">
            Close
          </button>
          <button className="px-4 py-2 bg-[#f9b30f] hover:bg-[#e0a40e] rounded-md text-sm font-medium text-white" onClick={() => {
          onNavigateToMessages(person.id);
          onClose();
        }} data-id="element-848">
            Create Message
          </button>
        </div>
      </div>
    </div>;
};